import React, { SetStateAction, useEffect, useMemo, useState } from "react";
import { RiSearchLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import TableContainer from "Components/Base/BaseTable";
import {
  checkStatusCodeSuccess,
  notFound,
  Search,
} from "Components/constants/Common";
import {
  RepairOrderLabel,
  NewLabel,
  REPAIR_PARTS_ORDER_LABEL,
  START_DATE_LABEL,
} from "Components/constants/RepairOrderOpening";
import { deleteROO, listOfROO } from "Api/RepairOrderOpen";
import { toast } from "react-toastify";
import DeleteModal from "Components/Base/DeleteModal";
import {
  ACTION_LABEL,
  CUST_NAME_N_ID_LABEL,
  RO_NO_LABEL,
  RO_TYPE_LABEL,
  TECHNICIAN_NAME_LABEL,
} from "Components/constants/ROModule";
import { ROReturnLabels } from "Components/constants/ReturnRONPO";
const RepairOrderOpening = () => {
  const [loader, setLoader] = useState<boolean>(false);
  const [ROO, setROO] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState<number>(0);
  const navigate = useNavigate();
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [ROOSearchData, setROOSearchData] = useState<Array<any>>([]);
  const [selectedROOId, setSelectedROOId] = useState<number | null>(null);
  const [columnName, setColumnName] = useState<string>("id");

  const handleFetchData = (page: any) => {
    setCurrentPage(page);
  };

  const openDeleteModal = (id: number) => {
    setSelectedROOId(id);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setSelectedROOId(null);
  };

  const handleFetchSorting = (
    page: any,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);

    if (id === "technician_name") {
      setColumnName("technician.technician_id");
    } else {
      setColumnName(id);
    }
    setSortOrder(order);
  };

  const fetchROOList = async () => {
    const payload = {
      limit,
      page: currentPage,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfROO(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setROO(res?.data?.repairOrder);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
        } else {
          setROO([]);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => setLoader(false));
  };

  const handleDeleteROO = () => {
    if (selectedROOId !== null) {
      setBtnLoader(true);
      deleteROO(selectedROOId)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            fetchROOList();
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message || error?.message);
        })
        .finally(() => {
          setBtnLoader(false);
          closeDeleteModal();
        });
    }
  };

  const fetchSearchROOData = () => {
    const payload = {
      limit,
      page: currentPage,
      search: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfROO(payload)
      .then((response) => {
        if (checkStatusCodeSuccess(response.statusCode)) {
          setROOSearchData(response?.data?.repairOrder || []);
          setTotalRecords(response?.data?.totalItems || 0);
          setTotalPages(response?.data?.totalPages);
          setCurrentPage(response?.data?.currentPage);
          setTotalNumberOfRows(response?.data?.numberOfRows);
          toast.success(response?.message);
        } else {
          setROOSearchData([]);
        }
      })
      .catch((error) => {
        setROOSearchData([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (searchValue) {
      fetchSearchROOData();
    } else {
      fetchROOList();
    }
  }, [currentPage, limit, searchValue, sortOrder, columnName]);

  const repairOrderColums = useMemo(
    () => [
      {
        header: RO_NO_LABEL,
        accessorKey: "ro_no",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: REPAIR_PARTS_ORDER_LABEL,
        accessorKey: "repair_part_order",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: START_DATE_LABEL,
        accessorKey: "start_date",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: TECHNICIAN_NAME_LABEL,
        accessorKey: "technician_name",
        cell: (cell: any) => {
          const technician = cell?.row?.original?.technician;
          return technician && technician.length > 0
            ? technician[0]?.technician?.technician_name
            : notFound.nullData;
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: CUST_NAME_N_ID_LABEL,
        accessorKey: "user_draft_id",
        cell: (cell: any) => {
          const customer = cell?.row?.original?.customer;
          const userDraftId = cell?.row?.original?.user_draft_id;
          return customer && userDraftId
            ? `${customer.name} (${userDraftId})`
            : "";
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: RO_TYPE_LABEL,
        accessorKey: "repair_order_type",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ACTION_LABEL,
        accessorKey: "ActionKey",
        cell: (cell: any) => (
          <div className="d-flex justify-content-start enable-action  gap-2 icon">
            <span className="tooltip-container">
              <i
                className="mdi mdi-pencil-outline fs-4 text-info cursor-pointer"
                onClick={() =>
                  navigate(`/editRepairOrder/${cell?.row?.original?.id}`)
                }
              ></i>
              <span className="tooltip-text">{ROReturnLabels.Edit}</span>
            </span>
            <span className="tooltip-container" title="Delete">
              <i
                className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"
                onClick={() => openDeleteModal(cell?.row?.original?.id)}
              ></i>
              <span className="tooltip-text">{ROReturnLabels.Delete}</span>
            </span>
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    []
  );
  document.title = RepairOrderLabel;

  return (
    <div className="page-content">
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{RepairOrderLabel}</span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={() => navigate("/addRepairOrder")}
              children={
                <>
                  <i className="mdi mdi-plus" /> {NewLabel}
                </>
              }
            />
          </div>
        </div>

        <div className="card py-2 px-4 overflow-x-auto">
          {loader || ROO.length > 0 || ROOSearchData.length > 0 ? (
            <TableContainer
              customPageSize={limit}
              setCustomPageSize={setLimit}
              isGlobalFilter={true}
              columns={repairOrderColums}
              data={searchValue ? ROOSearchData : ROO || []}
              tableClass="table bg-white"
              manualPagination={true}
              divClass="text-nowrap"
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              totalNumberOfRows={totalNumberOfRows}
              onSearch={setSearchValue}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}

          <DeleteModal
            show={deleteModal}
            onCloseClick={closeDeleteModal}
            onDeleteClick={handleDeleteROO}
            btnLoader={btnLoader}
          />
        </div>
      </div>
    </div>
  );
};

export default RepairOrderOpening;
